import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Chip,
  Card,
  CardActionArea,
  CardContent,
  useTheme,
  alpha,
} from '@mui/material';
import {
  FitnessCenter as FitnessIcon,
  LocalFireDepartment as FireIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

interface ProgrammeSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (programme: 'Foundation' | 'Ignition') => void;
  reportType: 'Initial' | 'Review';
  availableProgrammes: {
    foundation: boolean;
    ignition: boolean;
  };
}

export const ProgrammeSelectionModal: React.FC<ProgrammeSelectionModalProps> = ({
  open,
  onClose,
  onSelect,
  reportType,
  availableProgrammes,
}) => {
  const theme = useTheme();
  const [selectedProgramme, setSelectedProgramme] = React.useState<'Foundation' | 'Ignition' | null>(null);

  const handleSelect = () => {
    if (selectedProgramme) {
      onSelect(selectedProgramme);
      setSelectedProgramme(null);
    }
  };

  const handleClose = () => {
    setSelectedProgramme(null);
    onClose();
  };

  const cardVariants = {
    initial: { scale: 0.9, opacity: 0 },
    animate: { 
      scale: 1, 
      opacity: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 200,
        damping: 15,
      },
    },
    hover: {
      scale: 1.05,
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.98 },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" fontWeight={600}>
          Select Programme for {reportType} Report
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Multiple programmes have completed assessments. Please select which programme report to generate.
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Stack spacing={2}>
          <AnimatePresence>
            {availableProgrammes.foundation && (
              <motion.div
                variants={cardVariants}
                initial="initial"
                animate="animate"
                exit="initial"
                whileHover="hover"
                whileTap="tap"
              >
                <Card
                  elevation={selectedProgramme === 'Foundation' ? 4 : 1}
                  sx={{
                    border: selectedProgramme === 'Foundation' 
                      ? `2px solid ${theme.palette.primary.main}`
                      : `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    overflow: 'visible',
                  }}
                >
                  <CardActionArea onClick={() => setSelectedProgramme('Foundation')}>
                    <CardContent sx={{ p: 3 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          }}
                        >
                          <FitnessIcon sx={{ fontSize: 28 }} />
                        </Box>
                        
                        <Box flex={1}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="h6" fontWeight={600}>
                              Foundation Programme
                            </Typography>
                            <Chip
                              label="5/5 Complete"
                              size="small"
                              color="success"
                              sx={{ fontWeight: 500 }}
                            />
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Generate {reportType.toLowerCase()} assessment report for Foundation programme
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                  
                  {selectedProgramme === 'Foundation' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 2,
                      }}
                    >
                      <CheckIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                  )}
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {availableProgrammes.ignition && (
              <motion.div
                variants={cardVariants}
                initial="initial"
                animate="animate"
                exit="initial"
                whileHover="hover"
                whileTap="tap"
              >
                <Card
                  elevation={selectedProgramme === 'Ignition' ? 4 : 1}
                  sx={{
                    border: selectedProgramme === 'Ignition' 
                      ? `2px solid ${theme.palette.secondary.main}`
                      : `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    overflow: 'visible',
                  }}
                >
                  <CardActionArea onClick={() => setSelectedProgramme('Ignition')}>
                    <CardContent sx={{ p: 3 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                            color: theme.palette.secondary.main,
                          }}
                        >
                          <FireIcon sx={{ fontSize: 28 }} />
                        </Box>
                        
                        <Box flex={1}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="h6" fontWeight={600}>
                              Ignition Programme
                            </Typography>
                            <Chip
                              label="5/5 Complete"
                              size="small"
                              color="success"
                              sx={{ fontWeight: 500 }}
                            />
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Generate {reportType.toLowerCase()} assessment report for Ignition programme
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                  
                  {selectedProgramme === 'Ignition' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: theme.palette.secondary.main,
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 2,
                      }}
                    >
                      <CheckIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                  )}
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSelect}
          variant="contained"
          disabled={!selectedProgramme}
          sx={{ minWidth: 120 }}
        >
          Generate Report
        </Button>
      </DialogActions>
    </Dialog>
  );
};